// src/pages/Users/Techniques/components/TechniqueCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaInfoCircle, FaHeart, FaRegHeart, FaVolumeUp } from "react-icons/fa";
import { toAbsoluteTechniquePhotoUrl } from "../../../services/techniquesPhotoServices";


const FALLBACK_IMG = "/img/no-image.jpg";

function beltClasses(ceinture = "") {
  switch (String(ceinture).toLowerCase()) {
    case "jaune":
      return "bg-yellow-400 text-black";
    case "orange":
      return "bg-orange-400 text-black";
    case "verte":
      return "bg-green-400 text-black";
    case "bleue":
      return "bg-blue-500 text-white";
    case "marron":
      return "bg-amber-900 text-white";
    case "noire":
      return "bg-neutral-900 text-white";
    default:
      return "bg-slate-300 text-slate-900";
  }
}

// util pour fallback si l'image casse après chargement
function onImgError(e) {
  if (e?.currentTarget?.src !== window.location.origin + FALLBACK_IMG) {
    e.currentTarget.src = FALLBACK_IMG;
  }
}

// --- helpers TTS JP ---
const hasJPChars = (s = "") => /[\u3040-\u30FF\u4E00-\u9FFF]/.test(s);

function pickVoice(langStartsWith = "ja", fallbackLang = "ja-JP") {
  if (typeof window === "undefined" || !window.speechSynthesis) {
    return { voice: null, lang: fallbackLang };
  }
  const voices = window.speechSynthesis.getVoices?.() || [];
  const v = voices.find((vv) =>
    vv.lang?.toLowerCase().startsWith(langStartsWith)
  );
  return { voice: v || null, lang: v?.lang || fallbackLang };
}

function speak(text, langHint = "ja", fallbackLang = "ja-JP") {
  try {
    if (typeof window === "undefined" || !window.speechSynthesis || !text)
      return;
    const { voice, lang } = pickVoice(langHint, fallbackLang);
    const utt = new SpeechSynthesisUtterance(text);
    if (voice) utt.voice = voice;
    utt.lang = lang;
    utt.rate = 1;
    utt.pitch = 1;
    window.speechSynthesis.speak(utt);
  } catch {}
}

export default function TechniqueCard({
  technique,
  isFavorite,
  onToggleFavorite,
  onOpen,
  view = "grid", // "grid" | "list"
}) {
  const { image1_url, nom, famille, traduction, ceinture } = technique;
  // URL de l'image (ou fallback), utilisation de toAbsoluteTechniquePhotoUrl
  // pour gérer les URLs relatives /uploads/…
  // (mais pas pour le fallback)
  const imgSrc = toAbsoluteTechniquePhotoUrl(image1_url) || FALLBACK_IMG;

  // Import dynamique de wanakana (ou "wanakana" si coquille) pour toKana
  const [toKanaFn, setToKanaFn] = useState(null);
  const triedImport = useRef(false);

  useEffect(() => {
    if (triedImport.current) return;
    triedImport.current = true;
    (async () => {
      try {
        const mod = await import("wanakana"); // package correct
        if (mod?.toKana) {
          setToKanaFn(() => mod.toKana);
          return;
        }
      } catch {}
      try {
        // au cas où le package a été installé sous un nom erroné
        const mod2 = await import("wanakana");
        if (mod2?.toKana) {
          setToKanaFn(() => mod2.toKana);
        }
      } catch {}
    })();
  }, []);

  const jpForTTS = useMemo(() => {
    const jp = String(nom || "");
    if (!jp) return "";
    if (hasJPChars(jp)) return jp;
    // Nettoyage romaji léger pour TTS + conversion kana si possible
    const cleaned = jp.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
    return toKanaFn ? toKanaFn(cleaned) : cleaned;
  }, [nom, toKanaFn]);

  const openWithKeyboard = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };

  if (view === "list") {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/50 hover:shadow transition">
        <div
          className="shrink-0 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
          style={{ width: 90, aspectRatio: "551 / 617" }}
          role="button"
          tabIndex={0}
          aria-label={`Ouvrir ${nom}`}
          onClick={onOpen}
          onKeyDown={openWithKeyboard}
          title="Voir la fiche"
        >
          <img
            src={imgSrc}
            alt={nom}
            className="w-full h-full object-contain"
            loading="lazy"
            onError={onImgError}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`badge ${beltClasses(ceinture)} text-[10px]`}>
              {ceinture || "?"}
            </span>

            {/* Bouton audio JP (icon-only, style discret) */}
            <button
              className="w-7 h-7 grid place-items-center rounded-full ring-1 ring-slate-300 text-slate-600 hover:text-slate-900 hover:ring-sky-300 transition focus:outline-none focus:ring-2 focus:ring-violet-400"
              onClick={() => speak(jpForTTS, "ja", "ja-JP")}
              title="Prononcer (japonais)"
              aria-label="Prononcer le nom en japonais"
              type="button"
            >
              <FaVolumeUp className="text-sm" />
            </button>

            <button
              className="btn btn-ghost btn-xs rounded-full"
              onClick={onOpen}
              title="Voir la fiche"
            >
              <FaInfoCircle />
            </button>
            <button
              className={`btn btn-ghost btn-xs rounded-full ${
                isFavorite ? "text-red-500" : ""
              }`}
              onClick={onToggleFavorite}
              title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
            >
              {isFavorite ? <FaHeart /> : <FaRegHeart />}
            </button>
          </div>
          <p className="font-semibold truncate">{nom}</p>
          <p className="text-xs italic text-slate-600 dark:text-slate-300 truncate">
            {traduction}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            Famille : {famille}
          </p>
        </div>
      </div>
    );
  }

  // Vue grille
  return (
    <div className="group bg-base-100 shadow ring-1 ring-slate-200/70 dark:ring-slate-800/70 rounded-2xl overflow-hidden hover:shadow-lg transition h-full">
      <div
        className="w-full bg-slate-50 dark:bg-slate-900 flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
        style={{ aspectRatio: "551 / 617" }}
        role="button"
        tabIndex={0}
        aria-label={`Ouvrir ${nom}`}
        onClick={onOpen}
        onKeyDown={openWithKeyboard}
        title="Voir la fiche"
      >
        <img
          src={imgSrc}
          alt={nom}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={onImgError}
        />
      </div>

      <div className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className={`badge ${beltClasses(ceinture)} text-[10px]`}>
            {ceinture || "?"}
          </span>

          <div className="flex items-center gap-2">
            {/* Audio JP */}
            <button
              className="w-9 h-9 grid place-items-center rounded-xl ring-1 ring-slate-300 text-slate-600 hover:text-slate-900 hover:ring-sky-300 transition focus:outline-none focus:ring-2 focus:ring-violet-400"
              onClick={() => speak(jpForTTS, "ja", "ja-JP")}
              title="Prononcer (japonais)"
              aria-label="Prononcer le nom en japonais"
              type="button"
            >
              <FaVolumeUp className="text-base" />
            </button>

            <button
              className="badge badge-info text-white rounded-full cursor-pointer text-base p-3"
              onClick={onOpen}
              title="Voir la fiche"
              type="button"
            >
              <FaInfoCircle />
            </button>
            <button
              className={`badge ${
                isFavorite ? "text-red-500" : "text-slate-500"
              } badge-ghost rounded-full cursor-pointer text-base p-3`}
              onClick={onToggleFavorite}
              title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
              type="button"
            >
              {isFavorite ? <FaHeart /> : <FaRegHeart />}
            </button>
          </div>
        </div>

        <div>
          <p className="font-bold leading-tight">{nom}</p>
          <p className="italic text-xs text-slate-700 dark:text-slate-300">
            {traduction}
          </p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            Famille : {famille}
          </p>
        </div>
      </div>
    </div>
  );
}
